import React, { useEffect, useState } from 'react';

/**
 * Read-only Clinical Context renderer for treatment plan views.
 * Renders fields dynamically from the `treatment_plan_clinical_context` form template.
 * Used in the main plan view and client portal.
 */
export const TreatmentPlanClinicalContextReadOnly = ({
  plan,
  formTemplates,
  notCapturedMsg,
}) => {
  const [fields, setFields] = useState([]);

  const fieldDescriptions = {
    'loc': 'How will the provider/person served/parent/guardian know that level of care change is warranted and what plans are being discussed at this point in treatment to prepare?',
    'trauma': 'New reports of trauma, abuse, exploitation, or neglect and any actions taken in response to them.',
  };

  useEffect(() => {
    if (!formTemplates?.length) return;
    const template = formTemplates.find(t => t.form_type === 'treatment_plan_clinical_context');
    if (template?.form_fields) {
      const sorted = [...template.form_fields]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(f => ({
          ...f,
          description: f.description || fieldDescriptions[f.id] || '',
        }));
      setFields(sorted);
    }
  }, [formTemplates]);

  const fallback = notCapturedMsg || <p className="text-xs text-gray-400 italic">Not captured for this plan</p>;

  const defaultFields = [
    { id: 'dx', type: 'client_diagnoses', label: 'Diagnoses', sort_order: 1 },
    { id: 'meds', type: 'client_medications', label: 'Medications', sort_order: 2 },
    { id: 'loc', type: 'textarea', label: 'Level of Care Change', sort_order: 3, description: 'How will the provider/person served/parent/guardian know that level of care change is warranted and what plans are being discussed at this point in treatment to prepare?' },
    { id: 'trauma', type: 'textarea', label: 'New Reports of Trauma, Abuse, Exploitation, or Neglect', sort_order: 4 },
    { id: 'supports', type: 'supports_matrix', label: 'Community Supports and Resources', sort_order: 5 },
  ];

  const activeFields = fields.length > 0 ? fields : defaultFields;

  const getTextareaValue = (field) => {
    const label = (field.label || '').toLowerCase();
    if (label.includes('level of care')) return plan.level_of_care_notes;
    if (label.includes('trauma') || label.includes('abuse') || label.includes('neglect')) return plan.trauma_reports;
    return null;
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'client_diagnoses':
        return <ReadOnlyDiagnoses key={field.id} field={field} data={plan.diagnoses_snapshot} fallback={fallback} />;
      case 'client_medications':
        return <ReadOnlyMedications key={field.id} field={field} data={plan.medications_snapshot} fallback={fallback} />;
      case 'supports_matrix':
        return <ReadOnlySupports key={field.id} field={field} data={plan.supports} fallback={fallback} />;
      case 'textarea':
        return <ReadOnlyTextarea key={field.id} field={field} value={getTextareaValue(field)} fallback={fallback} />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3" data-testid="clinical-context-readonly">
      {activeFields.map(renderField)}
    </div>
  );
};

/* ─── Read-only sub-components ─── */

const ReadOnlyDiagnoses = ({ field, data, fallback }) => (
  <div className="mb-3" data-testid="clinical-context-diagnoses">
    <p className="text-xs font-medium text-gray-600 mb-1">{field.label}</p>
    {data?.length > 0 ? (
      <div className="space-y-1">
        {data.map((d, idx) => {
          const provNames = d.provider_names?.length > 0 ? d.provider_names.join(', ') : (d.assigned_by_name || '');
          const dxCode = d.code || d.diagnosis_code || '';
          const dxDesc = d.description || d.diagnosis_description || '';
          return (
            <div key={idx} className="text-xs flex items-center gap-2 bg-gray-50 p-1.5 rounded" data-testid={`clinical-context-diagnosis-${idx}`}>
              <span className="font-mono text-blue-600">{dxCode}</span>
              <span className="flex-1">{dxDesc}</span>
              {d.is_primary && <span className="text-xs bg-blue-100 text-blue-700 px-1 rounded">Primary</span>}
              {provNames && <span className="text-gray-400 text-[10px]">{provNames}</span>}
            </div>
          );
        })}
      </div>
    ) : fallback}
  </div>
);

const ReadOnlyMedications = ({ field, data, fallback }) => (
  <div className="mb-3" data-testid="clinical-context-medications">
    <p className="text-xs font-medium text-gray-600 mb-1">{field.label}</p>
    {data?.length > 0 ? (
      <div className="space-y-1">
        {data.map((m, idx) => (
          <div key={idx} className="text-xs flex items-center gap-2 bg-gray-50 p-1.5 rounded" data-testid={`clinical-context-medication-${idx}`}>
            <span className="font-medium flex-1">{m.name}</span>
            {m.dosage && <span className="text-gray-600">{m.dosage}</span>}
            {m.frequency && <span className="text-gray-500">| {m.frequency}</span>}
            {m.purpose && <span className="text-purple-600 italic">{m.purpose}</span>}
            {m.prescriber && <span className="text-gray-400 text-[10px]">({m.prescriber})</span>}
          </div>
        ))}
      </div>
    ) : fallback}
  </div>
);

const ReadOnlyTextarea = ({ field, value, fallback }) => (
  <div className="mb-3" data-testid={`clinical-context-textarea-${field.id}`}>
    <p className="text-xs font-medium text-gray-600 mb-1">{field.label}</p>
    {field.description && <p className="text-[11px] text-gray-400 mb-1">{field.description}</p>}
    {value ? <p className="text-xs text-gray-600 whitespace-pre-wrap">{value}</p> : fallback}
  </div>
);

const ReadOnlySupports = ({ field, data, fallback }) => (
  <div data-testid="clinical-context-supports">
    <p className="text-xs font-medium text-gray-600 mb-1">{field.label}</p>
    {data ? (
      data.none_reported ? (
        <p className="text-xs text-gray-500 italic">None Reported</p>
      ) : data.entries?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-1.5 border font-medium text-gray-600">Role</th>
                <th className="text-left p-1.5 border font-medium text-gray-600">Contact & Title</th>
                <th className="text-left p-1.5 border font-medium text-gray-600">Services Provided</th>
                <th className="text-left p-1.5 border font-medium text-gray-600 w-[80px]">Release</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((s, idx) => (
                <tr key={idx}>
                  <td className="p-1.5 border font-medium text-gray-700">{s.role}</td>
                  <td className="p-1.5 border text-gray-600">{s.contact_and_title || '—'}</td>
                  <td className="p-1.5 border text-gray-600">{s.services_provided || '—'}</td>
                  <td className="p-1.5 border text-gray-600">{s.release_signed || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : fallback
    ) : fallback}
  </div>
);

export default TreatmentPlanClinicalContextReadOnly;
